import React, { useState, useEffect } from "react";
import { useData } from "../../../context/DataContext";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import { MultiImageUploadField } from "../../../utils/ImageUploadField";

const HeroAdmin = () => {
  const {
    heroConfig,
    updateHeroConfig,
    uploadHeroBackground,
    deleteHeroBackgroundImage,
  } = useData();

  const [localConfig, setLocalConfig] = useState({
    heroText: "",
    highlightText: "",
    backgroundUrls: [],
  });

  const [bgFiles, setBgFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalConfig(heroConfig);
    setPreviewUrls(heroConfig.backgroundUrls || []);
    setBgFiles([]);
    setSlideIndex(0);
  }, [heroConfig]);

  useEffect(() => {
    if (!previewUrls.length) return;
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % previewUrls.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [previewUrls]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalConfig((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgFiles((prev) => [...prev, file]);
    setPreviewUrls((prev) => [...prev, URL.createObjectURL(file)]);
    setSlideIndex(previewUrls.length);
  };

  const handleRemoveImage = async (idx) => {
    const result = await Swal.fire({
      title: "Remove Image?",
      text: "Are you sure you want to remove this image from the carousel?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    });

    if (result.isConfirmed) {
      // If the image is already in the database (not just a new file)
      if (
        idx < (heroConfig.backgroundUrls || []).length &&
        bgFiles.length === 0
      ) {
        await deleteHeroBackgroundImage(idx);
        // The useEffect will update previewUrls automatically
      } else {
        // Remove from local new files
        setBgFiles((prev) => prev.filter((_, i) => i !== idx));
        setPreviewUrls((prev) => prev.filter((_, i) => i !== idx));
        if (slideIndex >= previewUrls.length - 1 && slideIndex > 0) {
          setSlideIndex(slideIndex - 1);
        }
      }
    }
  };

  const handleSave = async () => {
    const result = await Swal.fire({
      title: "Save Changes?",
      text: "Do you want to save the changes made to the hero section?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, save it!",
    });

    if (!result.isConfirmed) return;
    try {
      setSaving(true);

      // Step 1: Save text (heroText kept empty — only highlight is shown on the site)
      await updateHeroConfig({
        heroText: "",
        highlightText: localConfig.highlightText,
      });

      // Step 2: Upload new images and merge
      if (bgFiles.length > 0) {
        const uploadedUrls = await uploadHeroBackground(bgFiles);
        const updatedUrls = [
          ...(heroConfig.backgroundUrls || []),
          ...uploadedUrls,
        ];
        await updateHeroConfig({
          backgroundUrls: updatedUrls,
        });
      }

      toast.success("Hero section updated successfully.");
    } catch (err) {
      console.error(err);
      toast.error("Error saving hero section.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10">
      <h2 className="text-3xl font-bold text-gray-800">
        Hero Section Admin Panel
      </h2>

      {/* Hero Text Input */}
      <section className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-xl font-semibold text-gray-700">
          Hero Text Content
        </h3>
        <input
          name="highlightText"
          value={localConfig.highlightText}
          onChange={handleChange}
          placeholder="Hero Title (e.g., The Vivaak)"
          className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
        />
        <p className="text-xs text-gray-500">
          This text is shown at the bottom-center of the home hero image.
        </p>
      </section>

      {/* Background Image Section */}
      <section className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-xl font-semibold text-gray-700">
          Background Images (Carousel)
        </h3>
        <MultiImageUploadField
          label=""
          aspect="wide"
          hint="Recommended size: 1920×1080px (16:9)"
          existingUrls={heroConfig.backgroundUrls || []}
          newFiles={bgFiles}
          onAdd={(picked) => setBgFiles((prev) => [...prev, ...picked])}
          onRemove={async (idx, isExisting) => {
            if (isExisting) {
              const result = await Swal.fire({
                title: "Remove Image?",
                text: "Are you sure you want to remove this image from the carousel?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, remove it!",
              });
              if (result.isConfirmed) {
                await deleteHeroBackgroundImage(idx);
              }
            } else {
              setBgFiles((prev) => prev.filter((_, i) => i !== idx));
            }
          }}
        />
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className={`px-6 py-3 rounded-lg transition font-medium ${saving
            ? "bg-gray-400 text-white cursor-not-allowed"
            : "bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0]"
            }`}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default HeroAdmin;
